import React from "react";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Form, Row } from "react-bootstrap";
import { filter, get, groupBy, keyBy, map, size } from "lodash";
import { FiAlertCircle, FiGrid } from "react-icons/fi";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { toast } from "react-toastify";
import { payrollPeriodsToEnglishMapping } from "utils/timeHelper";
import { hasEnabled360PayrollIntegration } from "store/selectors/employer";

import {
  currentPayrolls,
  getAllEmployerGroups,
  getEmployeeInvitations,
  getEmployeeRoster,
  inviteEmployees,
  processRoster,
  resendInvitations,
  stageEmployeeInvites,
  updateProgressiveOnboarding,
} from "actions/employerActions";
import Button from "components/Button";
import Alert from "components/Alert";
import StatusBox from "components/StatusBox";
import moment from "moment";

import "./LaunchPlan.scss";
import { progressiveOnboardingEvents } from "statics/states";
import type { RootState } from "types/state";

const actions = [
  employerConstants.GET_EMPLOYER_GROUPS,
  employerConstants.STAGE_EMPLOYEE_INVITE,
  employerConstants.PROCESS_ROSTER,
  employerConstants.GET_EMPLOYEE_ROSTER,
  employerConstants.RESEND_INVITES_EMPLOYEE,
];

const isInvitingSelector = createLoadingSelector(actions);

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  const newRosterInvites = filter(
    state.employer.paginatedEmployeeRoster,
    (emp: { linkStatus?: unknown }) => emp.linkStatus === "UNKNOWN"
  );

  return {
    groups: state.employer.groups,
    error: errorSelector(state),
    isInviting: isInvitingSelector(state),
    employeeRosterCount: size(newRosterInvites),
    employeeInvitations: Object.values(
      state.employer.employeeInvitations
    ).filter((invite) => invite.status === "NEW"),
    groupIds: Object.values(state.employer.employeeInvitations).filter(
      (invite) => invite.groupId
    ),

    payrollsById: keyBy(state.employer.currentPayrolls, "groupId"),
    has360PayrollIntegration: hasEnabled360PayrollIntegration(state),
    hasEnabledPayrollIntegration: hasEnabled360PayrollIntegration(state),
  };
};

const mapDispatchToProps = {
  push,
  inviteEmployees,
  getEmployeeInvitations,
  stageEmployeeInvites,
  resendInvitations,
  updateProgressiveOnboarding,
  getAllEmployerGroups,
  processRoster,
  getEmployeeRoster,
  currentPayrolls,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onCancel?: () => void;
  onSuccess: () => void;
}

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  // read in _renderForm but never written by this component (set elsewhere /
  // pre-existing dead read) — optional to preserve runtime behavior.
  showAcceptedError?: boolean;
}

class LaunchPlan extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  componentDidMount() {
    // PRE-EXISTING BUG (preserved): currentPayrolls's action creator requires a
    // second `companyId` argument, but this component has always called it with
    // only the client. Suppressing the arity error to keep behavior identical.
    // @ts-expect-error pre-existing missing `companyId` arg — preserved as-is
    void this.props.currentPayrolls(this.props.client as ApolloClient<object>);
    void this.props.getEmployeeInvitations(
      this.props.client as ApolloClient<object>
    );
    if (this.props.hasEnabledPayrollIntegration) {
      void this.props.getEmployeeRoster(
        this.props.client as ApolloClient<object>,
        {
          offset: 0,
          limit: 0,
        }
      );
    }
  }

  // A LaunchedPlan response flips hasFullyOnboarded, which unmounts this card
  // before processRoster resolves — so roster failures are reported by toast.
  _launchIntegratedPlan = async () => {
    // @ts-expect-error pre-existing missing `companyId` arg — preserved as-is
    const launch = await this.props.updateProgressiveOnboarding(
      this.props.client as ApolloClient<object>,
      progressiveOnboardingEvents.LaunchPlan
    );
    if (!launch.success) {
      toast.error(launch.error || "Failed to launch plan.");
      return;
    }

    const roster = await this.props.processRoster(
      this.props.client as ApolloClient<object>
    );
    if (!roster.success) {
      toast.error(
        roster.error || "Plan launched, but the roster failed to sync."
      );
      return;
    }

    this.props.onSuccess();
    toast.success("Successfully launched plan.");
  };

  _handleOnClick = () => {
    const groupIds = map(this.props.groups, "id");

    if (this.props.hasEnabledPayrollIntegration) {
      return this._launchIntegratedPlan();
    }

    void this.props
      .resendInvitations(this.props.client as ApolloClient<object>, {
        // The service params expect `invitations`; this site only ever resends by
        // group.
        // @ts-expect-error pre-existing resendInvitations param shape — preserved as-is
        groupIds,
      })
      .then(async (result) => {
        if (!result.success) {
          return;
        }
        // @ts-expect-error pre-existing missing `companyId` arg — preserved as-is
        await this.props.updateProgressiveOnboarding(
          this.props.client as ApolloClient<object>,
          progressiveOnboardingEvents.LaunchPlan
        );
        this.props.onSuccess();
        toast.success("Successfully launched plan.");
      });
  };

  _getGroupInfo = () => {
    const commonDateFormat = "MMM Do YYYY";

    const employeeInvitationsByGroupId = groupBy(
      this.props.employeeInvitations,
      "groupId"
    );

    return map(this.props.groups, (group) => {
      // group is Record<string, unknown> from the employer slice; narrow the
      // fields read below to match how they are used at runtime.
      const groupName = group.name as React.ReactNode;
      const planType = group.planType as string;
      const payrollFrequency = group.payrollFrequency as string;
      const groupId = group.id as string;

      const payrollFrequencyLabel =
        payrollPeriodsToEnglishMapping[
          payrollFrequency as keyof typeof payrollPeriodsToEnglishMapping
        ];

      const numOfInvites = size(employeeInvitationsByGroupId[groupId]);

      const processingDate = get(
        this.props.payrollsById[groupId],
        "payrollProcessingDate"
      );

      return (
        <>
          <div className="invite-box">
            <div className="invite-group">
              <div className="launch-circle">
                <span className="launch-icon">
                  <FiGrid color="white" stroke="#60A4BF" size={16} />
                </span>
              </div>

              <div className="group">
                <div className="group-details">
                  <p className="group-name">{groupName} </p>
                </div>
              </div>
            </div>

            <span className="type">
              <StatusBox type="plan-type" status={planType} />
            </span>
          </div>

          <span className="scheudle">
            <span className="info">
              <p className="scheudle-info">Frequency:</p>
              <p className="scheudle-info">{payrollFrequencyLabel}</p>
            </span>

            <span className="info">
              <p className="scheudle-info">Processing Date:</p>
              <p className="scheudle-info">
                {moment(processingDate as moment.MomentInput).format(
                  commonDateFormat
                )}
              </p>
            </span>
            <span className="info">
              <p className="scheudle-info">Employee Invitations: </p>

              <p className="scheudle-info">
                {this.props.hasEnabledPayrollIntegration
                  ? this.props.employeeRosterCount
                  : numOfInvites}
              </p>
            </span>
          </span>
        </>
      );
    });
  };

  _renderForm() {
    return (
      <div className="launch-plan">
        <div className="download-employee-template">
          <div className="main-content">
            <Row>
              <Col>
                <Card>
                  <Card.Title>
                    <div className="launch">
                      <p className="launch-header">
                        Invite your team{" "}
                        <img
                          alt="Icon"
                          height={"20"}
                          src="/assets/congrats.svg"
                        />
                      </p>
                      <p className="launch-subtext">
                        You have created a new invite list. Invite your team to
                        start helping them save for retirement.
                      </p>
                    </div>
                  </Card.Title>
                  <Card.Body>{this._getGroupInfo()}</Card.Body>
                  <Form.Group as={Col} className="send-invite">
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                    {this.state.showAcceptedError && (
                      <Alert
                        type="error"
                        msg={
                          "Employee has already been invited and started enrollment. Check the Dashboard Enrolling list. "
                        }
                      />
                    )}

                    <div className="submit-row">
                      <span className="cancel-btn">
                        <Button
                          btnLabel="Cancel"
                          name="cancel"
                          color="cancel"
                          onClick={this.props.onCancel}
                        />
                      </span>
                      <Button
                        btnLabel="Launch Plan"
                        name="submit"
                        withArrow={true}
                        onClick={this._handleOnClick}
                        loading={this.props.isInviting}
                      />
                    </div>
                  </Form.Group>
                  {this.props.has360PayrollIntegration && (
                    <Form.Group as={Col}>
                      <div className="info-box">
                        <span className="info-icon-container">
                          <FiAlertCircle
                            size={16}
                            fill={"#FFFFFF"}
                            color={"#FFFFFF"}
                            stroke={"#01a3b0"}
                            strokeWidth={"1.5"}
                          />
                        </span>
                        <span className="info-text">
                          After launching your plan, invitations will be sent
                          out within the next hour.
                        </span>
                      </div>
                    </Form.Group>
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const content = this._renderForm();

    return (
      <div className="add-employee-form" style={{ marginBottom: 8 }}>
        {content}
      </div>
    );
  }
}

export default connector(withApollo<Props>(LaunchPlan));
